import { Link, createFileRoute } from "@tanstack/react-router";

import { AdminCard, ListState } from "@/components/admin/primitives";
import { LinkCard } from "@/components/revendedor/LinkCard";
import { formatDate } from "@/lib/dates";
import { countLabel, formatBRL } from "@/lib/format";
import { useResellerMe, useResellerOrders } from "@/lib/resellers";
import { useStoreSettings } from "@/lib/settings";

export const Route = createFileRoute("/revendedor/_area/")({
  head: () => ({ meta: [{ title: "Início — Revenda MonteBox" }] }),
  component: ResellerHome,
});

const rate = (value: number) => `${String(value).replace(".", ",")}%`;

function ResellerHome() {
  const me = useResellerMe();
  const orders = useResellerOrders();
  const data = me.data;

  if (!data) {
    return (
      <AdminCard>
        <ListState loading={me.isLoading} error={me.error} empty={!me.isLoading} emptyLabel="—" />
      </AdminCard>
    );
  }

  const tiles = [
    {
      label: "Vendas este mês",
      value: String(data.stats.salesThisMonth),
      note: formatBRL(data.stats.revenueThisMonth),
    },
    { label: "Clientes na carteira", value: String(data.stats.customers), note: "vínculos ativos" },
    {
      label: "Comissão este mês",
      value: formatBRL(data.commissions.thisMonth),
      note: `${countLabel(data.stats.sales, "venda", "vendas")} no total`,
    },
    {
      label: "Pedidos em aberto",
      value: String(data.stats.openOrders),
      note: "do seu link e da carteira, ainda não pagos",
    },
  ];
  const recent = (orders.data ?? []).slice(0, 5);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-mb-navy">
          Olá, {data.name.split(" ")[0]}
        </h1>
        <p className="mt-1 text-sm text-mb-muted">
          Sua revenda autorizada MonteBox. Seus clientes montam, enviam e aprovam o pedido pelo
          site; aqui você acompanha cada pedido, sua carteira e sua comissão.
        </p>
      </div>

      <LinkCard reseller={data} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((tile) => (
          <AdminCard key={tile.label} className="p-5">
            <p className="text-xs font-bold text-mb-muted">{tile.label}</p>
            <p className="mt-1.5 font-display text-2xl font-bold text-mb-navy">{tile.value}</p>
            <p className="mt-0.5 text-xs text-mb-muted">{tile.note}</p>
          </AdminCard>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <AdminCard className="p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-bold text-mb-navy">Últimos pedidos</h2>
            <Link
              to="/revendedor/vendas"
              className="text-xs font-bold text-mb-navy hover:underline"
            >
              Ver todos
            </Link>
          </div>
          {recent.length === 0 ? (
            <p className="py-6 text-center text-sm text-mb-muted">
              Nenhum pedido ainda. Divulgue seu link para começar.
            </p>
          ) : (
            <ul className="mt-3 flex flex-col">
              {recent.map((order) => (
                <li
                  key={order.id}
                  className="flex items-center justify-between gap-3 border-t border-mb-line py-2.5 text-sm first:border-t-0"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-mb-ink">{order.customerName}</p>
                    <p className="text-xs text-mb-muted">
                      {formatDate(order.createdAt)} · {countLabel(order.pieces, "peça", "peças")}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-semibold tabular-nums text-mb-ink">
                      {formatBRL(order.value)}
                    </p>
                    <p className="text-[11px] text-mb-muted">
                      {order.canceled
                        ? "Cancelado"
                        : `${order.kind === "venda" ? "Venda" : "Em aberto"} · ${order.status}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </AdminCard>

        <AdminCard className="p-6">
          <h2 className="font-display text-base font-bold text-mb-navy">Sua comissão</h2>
          <p className="mt-1 text-xs text-mb-muted">
            Sobre o valor das peças (sem frete), em toda venda paga de cliente da sua carteira —
            inclusive quando ele volta a comprar.
          </p>
          <p className="mt-4 font-display text-3xl font-bold text-mb-navy">
            {rate(data.defaultRate)}
          </p>
          <p className="text-xs text-mb-muted">taxa geral</p>
          {data.categoryRates.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1 text-sm">
              {data.categoryRates.map((row) => (
                <li key={row.categoryId} className="flex justify-between">
                  <span className="text-mb-ink">{row.label}</span>
                  <span className="font-semibold tabular-nums text-mb-ink">
                    {rate(row.rate ?? 0)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-mb-line pt-4 text-center">
            {(
              [
                ["A liberar", data.commissions.pending],
                ["Liberada", data.commissions.released],
                ["Paga", data.commissions.paid],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <dt className="text-[11px] font-bold text-mb-muted">{label}</dt>
                <dd className="mt-0.5 font-semibold tabular-nums text-mb-ink">
                  {formatBRL(value)}
                </dd>
              </div>
            ))}
          </dl>
          <Link
            to="/revendedor/comissoes"
            className="mt-4 block text-center text-xs font-bold text-mb-navy hover:underline"
          >
            Ver extrato e repasses
          </Link>
        </AdminCard>
      </div>
    </div>
  );
}
